import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthStore } from '@vanialabs/core-auth';
import { getDashboardSpotlight } from '../services/dashboardSpotlight.service';
import { resolveAccessToken } from '../../../app/auth/authToken';
import { dashboardCache, scopedCacheKey } from '../cache/dashboardCache';
import type { DashboardSpotlight } from '../types/dashboard.types';

const CACHE_NAMESPACE = 'dashboard-spotlight';

type CachedSpotlight = DashboardSpotlight | null;

export function useDashboardSpotlight() {
  const isAuthInitialized = useAuthStore((state) => state.isInitialized);
  const session = useAuthStore((state) => state.session);
  const accessToken = resolveAccessToken(session);

  const cacheKey = scopedCacheKey(CACHE_NAMESPACE, accessToken);

  const [spotlight, setSpotlight] = useState<CachedSpotlight>(
    () => dashboardCache.get<CachedSpotlight>(cacheKey) ?? null
  );
  // True only while there is nothing cached to show yet (first visit).
  const [isInitialLoading, setIsInitialLoading] = useState(
    () => dashboardCache.get<CachedSpotlight>(cacheKey) === null
  );
  // True while cached data is already on screen and we're fetching fresh
  // data behind it. The UI intentionally does not react to this.
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const requestIdRef = useRef(0);

  const load = useCallback(() => {
    if (!isAuthInitialized) {
      return;
    }

    if (!accessToken) {
      requestIdRef.current += 1;
      setSpotlight(null);
      setError(null);
      setIsInitialLoading(false);
      setIsRefreshing(false);
      return;
    }

    const requestId = ++requestIdRef.current;
    const cached = dashboardCache.get<CachedSpotlight>(cacheKey);
    const hasCached = cached !== null;

    if (hasCached) {
      // Cache-first: show the cached card immediately, refresh silently.
      setSpotlight(cached);
      setIsInitialLoading(false);
      setIsRefreshing(true);
    } else {
      setIsInitialLoading(true);
    }
    setError(null);

    getDashboardSpotlight()
      .then((data) => {
        if (requestIdRef.current !== requestId) return;

        dashboardCache.set(cacheKey, data);
        setSpotlight(data);
        setIsInitialLoading(false);
        setIsRefreshing(false);
      })
      .catch((err: unknown) => {
        if (requestIdRef.current !== requestId) return;

        // A background refresh failure should never blank out a card the
        // user is already looking at -- only surface the error state when
        // there was nothing cached to fall back to.
        if (!hasCached) {
          setSpotlight(null);
          setError(
            err instanceof Error ? err : new Error('Failed to load spotlight')
          );
        }
        setIsInitialLoading(false);
        setIsRefreshing(false);
      });
  }, [isAuthInitialized, accessToken, cacheKey]);

  useEffect(() => {
    load();
  }, [load]);

  const retry = useCallback(() => {
    load();
  }, [load]);

  return {
    spotlight,
    isInitialLoading,
    isRefreshing,
    error,
    retry,
  };
}
